import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PifSidebar } from '@/components/PifSidebar';
import { CompanyGrid } from '@/components/CompanyGrid';
import { MobileSidebarDrawer } from '@/components/Layout';
import { useCategories, useFilteredSheets } from '@/hooks/useCategories';
import { useAccounts } from '@/hooks/useAccounts';
import { useMobile } from '@/hooks/useMobile';
import { useSheetSelection } from '@/contexts/sheetSelection';
import http from '@/services/http';
import type { CompanySheet } from '@/types/category';

const getMobileFiltersSeenKey = (accountId: number) => `pif-mobile-filters-seen:${accountId}`;

const normalizeSearchText = (value: string | null | undefined) =>
  (value ?? '').toLowerCase().replace(/\s+/g, ' ').trim();

const matchesCompanySearch = (sheet: CompanySheet, normalizedQuery: string) => {
  if (!normalizedQuery) {
    return false;
  }

  return [sheet.display_name, sheet.title, sheet.description]
    .some((value) => normalizeSearchText(value).includes(normalizedQuery));
};

export function BrowseView() {
  const { accountId: accountIdParam } = useParams<{ accountId: string }>();
  const { accounts } = useAccounts();
  const navigate = useNavigate();
  const isMobile = useMobile();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const { setSelectedSheetId, setSelectedCategoryIds: setContextCategoryIds } = useSheetSelection();

  useEffect(() => {
    setSelectedSheetId(null);
    setContextCategoryIds([]);
  }, [setSelectedSheetId, setContextCategoryIds]);

  useEffect(() => {
    if (!isMobile) setMobileSidebarOpen(false);
  }, [isMobile]);

  const rawId = accountIdParam ?? accounts[0]?.id;
  const accountId: number | null = rawId ? parseInt(rawId, 10) : null;

  const { categories, loading: catLoading } = useCategories(accountId);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<Set<number>>(new Set());
  const [sectorMap, setSectorMap] = useState<Map<number, string>>(new Map());
  const [categorySheetsIndex, setCategorySheetsIndex] = useState<Map<number, CompanySheet[]>>(new Map());
  const [hasSeenMobileFilters, setHasSeenMobileFilters] = useState(false);

  const categoryIdsArray = useMemo(() => Array.from(selectedCategoryIds), [selectedCategoryIds]);
  const { sheets: companies, loading: sheetsLoading } = useFilteredSheets(
    accountId,
    categoryIdsArray
  );

  const [allSheets, setAllSheets] = useState<CompanySheet[]>([]);
  const [allSheetsLoading, setAllSheetsLoading] = useState(false);
  const [sidebarSearch, setSidebarSearch] = useState('');
  const showAll = selectedCategoryIds.size === 0;
  const normalizedSidebarSearch = normalizeSearchText(sidebarSearch);

  useEffect(() => {
    setSelectedCategoryIds(new Set());
    setSidebarSearch('');
  }, [accountId]);

  useEffect(() => {
    if (typeof window === 'undefined' || !accountId) {
      setHasSeenMobileFilters(false);
      return;
    }

    setHasSeenMobileFilters(window.sessionStorage.getItem(getMobileFiltersSeenKey(accountId)) === '1');
  }, [accountId]);

  const markMobileFiltersSeen = useCallback(() => {
    if (typeof window === 'undefined' || !accountId) {
      return;
    }

    window.sessionStorage.setItem(getMobileFiltersSeenKey(accountId), '1');
    setHasSeenMobileFilters(true);
  }, [accountId]);

  useEffect(() => {
    if (!isMobile || !accountId || hasSeenMobileFilters || selectedCategoryIds.size > 0) {
      return;
    }

    setMobileSidebarOpen(true);
  }, [accountId, hasSeenMobileFilters, isMobile, selectedCategoryIds.size]);

  useEffect(() => {
    if (!showAll || !accountId) return;
    setAllSheetsLoading(true);
    http
      .get(`/v1/accounts/${accountId}/sheets`)
      .then((res) => {
        setAllSheets(
          res.data.map(
            (s: {
              id: number;
              title: string;
              sheet_color?: string;
              logo_url?: string | null;
              display_name?: string | null;
              description?: string | null;
            }) => ({
              id: s.id,
              title: s.title,
              display_name: s.display_name ?? null,
              logo_url: s.logo_url ?? null,
              description: s.description ?? null,
              sheet_color: s.sheet_color ?? null,
            })
          )
        );
      })
      .catch(console.error)
      .finally(() => setAllSheetsLoading(false));
  }, [showAll, accountId]);

  useEffect(() => {
    if (!accountId || !categories) {
      setSectorMap(new Map());
      return;
    }

    const sectorGroup = categories.groups.find((g) => g.group === "sector");
    const sectorCategories = sectorGroup?.categories ?? [];
    if (sectorCategories.length === 0) {
      setSectorMap(new Map());
      return;
    }

    let cancelled = false;
    const loadSectorMap = async () => {
      try {
        const responses = await Promise.all(
          sectorCategories.map((category) =>
            http
              .get(`/v1/accounts/${accountId}/categories/${category.id}/sheets`)
              .then((res) => ({ name: category.name, sheets: res.data as CompanySheet[] }))
          )
        );

        if (cancelled) return;
        const next = new Map<number, string>();
        for (const response of responses) {
          for (const sheet of response.sheets) {
            if (!next.has(sheet.id)) {
              next.set(sheet.id, response.name);
            }
          }
        }
        setSectorMap(next);
      } catch (error) {
        if (import.meta.env?.DEV) {
          console.error("Failed to build sector map", error);
        }
      }
    };

    void loadSectorMap();
    return () => {
      cancelled = true;
    };
  }, [accountId, categories]);

  useEffect(() => {
    if (!accountId || !categories) {
      setCategorySheetsIndex(new Map());
      return;
    }

    const searchableCategories = categories.groups
      .flatMap((group) => group.categories)
      .filter((category) => category.sheet_count > 0);

    if (searchableCategories.length === 0) {
      setCategorySheetsIndex(new Map());
      return;
    }

    let cancelled = false;

    const loadCategorySheetsIndex = async () => {
      try {
        const responses = await Promise.all(
          searchableCategories.map((category) =>
            http
              .get(`/v1/accounts/${accountId}/categories/${category.id}/sheets`)
              .then((res) => ({ categoryId: category.id, sheets: res.data as CompanySheet[] }))
          )
        );

        if (cancelled) return;

        const next = new Map<number, CompanySheet[]>();
        for (const response of responses) {
          next.set(response.categoryId, response.sheets);
        }

        setCategorySheetsIndex(next);
      } catch (error) {
        if (import.meta.env?.DEV) {
          console.error('Failed to build category sheets index', error);
        }
      }
    };

    void loadCategorySheetsIndex();

    return () => {
      cancelled = true;
    };
  }, [accountId, categories]);

  const categoryNameMatches = useMemo(() => {
    const next = new Set<number>();
    if (!normalizedSidebarSearch) {
      return next;
    }

    for (const group of categories?.groups ?? []) {
      for (const category of group.categories) {
        if (normalizeSearchText(category.name).includes(normalizedSidebarSearch)) {
          next.add(category.id);
        }
      }
    }

    return next;
  }, [categories, normalizedSidebarSearch]);

  const categoryCompanyMatches = useMemo(() => {
    if (!normalizedSidebarSearch) {
      return new Set<number>();
    }

    const next = new Set<number>();
    for (const [categoryId, sheets] of categorySheetsIndex) {
      if (sheets.some((sheet) => matchesCompanySearch(sheet, normalizedSidebarSearch))) {
        next.add(categoryId);
      }
    }

    return next;
  }, [categorySheetsIndex, normalizedSidebarSearch]);

  const displayCompanies = useMemo(() => {
    const baseCompanies = showAll ? allSheets : companies;
    if (!normalizedSidebarSearch) {
      return baseCompanies;
    }

    const companyMatches = baseCompanies.filter((sheet) => matchesCompanySearch(sheet, normalizedSidebarSearch));
    if (companyMatches.length > 0) {
      return companyMatches;
    }

    const resolvedCategoryIds = selectedCategoryIds.size > 0
      ? Array.from(selectedCategoryIds).filter((categoryId) => categoryNameMatches.has(categoryId))
      : Array.from(categoryNameMatches);

    if (resolvedCategoryIds.length === 0) {
      return [];
    }

    const next = new Map<number, CompanySheet>();
    for (const categoryId of resolvedCategoryIds) {
      const categorySheets = categorySheetsIndex.get(categoryId) ?? [];
      for (const sheet of categorySheets) {
        next.set(sheet.id, sheet);
      }
    }

    return Array.from(next.values());
  }, [allSheets, categoryNameMatches, categorySheetsIndex, companies, normalizedSidebarSearch, selectedCategoryIds, showAll]);

  const companyCount = displayCompanies.length;
  const loading = catLoading || (showAll ? allSheetsLoading : sheetsLoading);
  const isGuidedMobileFilters = isMobile && mobileSidebarOpen && !hasSeenMobileFilters;
  const companyResultsLabel = `${companyCount} compan${companyCount === 1 ? 'y' : 'ies'}`;

  const closeMobileSidebar = useCallback(() => {
    markMobileFiltersSeen();
    setMobileSidebarOpen(false);
  }, [markMobileFiltersSeen]);

  const handleToggleCategory = useCallback((id: number) => {
    setSelectedCategoryIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleResetFilters = useCallback(() => {
    setSelectedCategoryIds(new Set());
    setSidebarSearch('');
  }, []);

  const handleCompanyClick = useCallback(
    (id: number) => {
      const base = accountId ? `/account/${accountId}` : '';
      navigate(`${base}/chat/new?sheetId=${id}`);
    },
    [accountId, navigate]
  );

  const handleChatAboutCategories = useCallback(() => {
    if (selectedCategoryIds.size === 0) return;
    const ids = Array.from(selectedCategoryIds).join(',');
    const base = accountId ? `/account/${accountId}` : '';
    navigate(`${base}/chat/new?categoryIds=${ids}`);
  }, [selectedCategoryIds, accountId, navigate]);

  return (
    <div className="flex h-screen flex-col gap-3 bg-peach p-3 md:flex-row md:gap-6 md:p-6">
      <MobileSidebarDrawer
        open={isMobile && mobileSidebarOpen}
        onClose={closeMobileSidebar}
        showHeader={false}
        showCloseButton={!isGuidedMobileFilters}
        footer={(
          <div className="flex flex-col gap-2">
            <p className="text-sm text-grey-hover tracking-[0.3px]">
              {loading
                ? 'Loading companies...'
                : selectedCategoryIds.size > 0
                  ? `Showing ${companyResultsLabel}`
                  : normalizedSidebarSearch
                    ? 'Select a filter to narrow companies'
                    : 'Pick categories to narrow companies'}
            </p>
            <button
              type="button"
              onClick={closeMobileSidebar}
              className="flex h-11 w-full items-center justify-center rounded-md bg-pif-green px-4 text-md text-white transition-colors hover:bg-pif-green-light"
            >
              {selectedCategoryIds.size > 0 ? `Show ${companyResultsLabel}` : 'Browse all companies'}
            </button>
          </div>
        )}
      >
        <div className="h-full">
          <PifSidebar
            categories={categories}
            categoryCompanyMatches={categoryCompanyMatches}
            selectedCategoryIds={selectedCategoryIds}
            onToggleCategory={handleToggleCategory}
            onResetFilters={handleResetFilters}
            loading={catLoading}
            search={sidebarSearch}
            onSearchChange={setSidebarSearch}
          />
        </div>
      </MobileSidebarDrawer>

      {/* Sidebar */}
      <div className="hidden w-[300px] shrink-0 md:block">
        <PifSidebar
          categories={categories}
          categoryCompanyMatches={categoryCompanyMatches}
          selectedCategoryIds={selectedCategoryIds}
          onToggleCategory={handleToggleCategory}
          onResetFilters={handleResetFilters}
          loading={catLoading}
          search={sidebarSearch}
          onSearchChange={setSidebarSearch}
        />
      </div>

      {/* Main content */}
      <div className="flex min-w-0 flex-1 flex-col gap-3 md:gap-6">
        <div className="flex h-14 items-center justify-between rounded-lg border border-peach-1 bg-white px-3 md:hidden">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setMobileSidebarOpen(true)}
              className="flex h-9 w-9 items-center justify-center rounded-md border border-peach-1 hover:bg-peach transition-colors"
              aria-label="Open filters"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M2.5 4h11M4.5 8h7M6.5 12h3"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            <h1 className="text-md font-bold text-grey-black tracking-[0.3px]">
              {selectedCategoryIds.size > 0
                ? `Filtered · ${selectedCategoryIds.size}`
                : 'Companies'}
            </h1>
          </div>
          <button
            onClick={handleChatAboutCategories}
            disabled={selectedCategoryIds.size === 0}
            className={`
              flex items-center gap-2 h-9 px-3 rounded-md text-sm text-white transition-colors
              ${
                selectedCategoryIds.size > 0
                  ? 'bg-pif-green hover:bg-pif-green-light'
                  : 'bg-grey-medium cursor-not-allowed'
              }
            `}
          >
            <span>Chat</span>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" className="shrink-0">
              <path
                d="M3 8h10M9 4l4 4-4 4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {/* Header bar */}
        <div className="hidden md:flex bg-white border border-peach-1 rounded-lg h-[84px] items-center justify-between px-5 shrink-0">
          <h1 className="text-lg font-bold text-grey-black tracking-[0.3px]">
            {selectedCategoryIds.size > 0 ? 'Filtered companies' : 'All companies'}
          </h1>
          <button
            onClick={handleChatAboutCategories}
            disabled={selectedCategoryIds.size === 0}
            className={`
              flex items-center gap-2 h-11 px-4 rounded-md text-md text-white transition-colors
              ${
                selectedCategoryIds.size > 0
                  ? 'bg-pif-green hover:bg-pif-green-light'
                  : 'bg-grey-medium cursor-not-allowed'
              }
            `}
          >
            <span>Chat about selected companies</span>
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              className="shrink-0"
            >
              <path
                d="M3 8h10M9 4l4 4-4 4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64 text-grey-hover text-lg">
              Loading...
            </div>
          ) : (
            <CompanyGrid
              companies={displayCompanies}
              onCompanyClick={handleCompanyClick}
              sectorMap={sectorMap}
            />
          )}
        </div>
      </div>
    </div>
  );
}
